import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient';

export interface UserInfoData {
  upn?: string;
  id?: string;
  displayName?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  accountEnabled?: boolean | null;
  mail?: string | null;
  riskLevel?: string | null;
  mfaRegistered?: boolean | null;
  lastSignIn?: {
    ipAddress?: string;
    location?: { city?: string; state?: string; countryOrRegion?: string };
    createdDateTime?: string;
    status?: { errorCode?: number; failureReason?: string };
    appDisplayName?: string;
    clientAppUsed?: string;
  } | null;
  recentSignInCount24h?: number;
  note?: string;
}

interface Props {
  upn: string;
  customerId?: string;
  onRunKql?: (query: string) => void;
}

function riskBadge(level: string | null | undefined): { label: string; cls: string } {
  const v = (level ?? '').toLowerCase();
  if (v === 'high') return { label: 'High Risk', cls: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' };
  if (v === 'medium') return { label: 'Medium Risk', cls: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300' };
  if (v === 'low') return { label: 'Low Risk', cls: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' };
  if (v === 'none' || v === 'hidden') return { label: 'No Risk', cls: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' };
  return { label: 'Risk: Unknown', cls: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' };
}

function formatLocation(loc: { city?: string; state?: string; countryOrRegion?: string } | undefined): string {
  if (!loc || typeof loc !== 'object') return '';
  return [loc.city, loc.state, loc.countryOrRegion].filter(Boolean).join(', ');
}

export function UserInfoPanel({ upn, customerId, onRunKql }: Props) {
  const [data, setData] = useState<UserInfoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 412 from /user-info means the customer has no app-registration creds
  // configured; surface a dedicated badge instead of a raw error string so
  // operators know exactly what to do.
  const [noCredentials, setNoCredentials] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setNoCredentials(null);
    if (!upn) return;
    setLoading(true);
    const params = new URLSearchParams({ upn });
    if (customerId) params.set('customerId', customerId);
    apiFetch<UserInfoData>(`/user-info?${params.toString()}`)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 412 && e.body['error'] === 'no_credentials') {
          setNoCredentials(typeof e.body['message'] === 'string' ? (e.body['message'] as string) : e.message);
        } else {
          setError(String(e instanceof Error ? e.message : e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [upn, customerId]);

  if (!upn) return null;

  function huntSignins() {
    if (!onRunKql) return;
    onRunKql([
      'SigninLogs',
      '| where TimeGenerated > ago(7d)',
      `| where UserPrincipalName == "${upn.replace(/"/g, '\\"')}"`,
      '| project TimeGenerated, IPAddress, Location, AppDisplayName, ResultType, ResultDescription, ConditionalAccessStatus',
      '| order by TimeGenerated desc',
      '| take 100',
    ].join('\n'));
  }

  const risk = riskBadge(data?.riskLevel);

  return (
    <div className="bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-900 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-cyan-700 dark:text-cyan-300 font-semibold">User Identity</p>
          <code className="text-[11px] text-foreground bg-card px-1.5 py-0.5 rounded border border-border">{upn}</code>
        </div>
        {onRunKql && (
          <button
            onClick={huntSignins}
            className="text-[11px] bg-cyan-600 text-white px-2 py-1 rounded font-semibold hover:bg-cyan-700 transition-colors"
          >
            Hunt 7d sign-ins
          </button>
        )}
      </div>

      {loading && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <span className="animate-pulse">●</span> Looking up Entra ID profile…
        </div>
      )}
      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-2 py-1.5">
          {error}
        </div>
      )}
      {noCredentials && (
        <div className="text-xs bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded px-2 py-1.5 flex items-start gap-2">
          <span className="text-[10px] uppercase tracking-wide bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded font-bold shrink-0">No Creds</span>
          <span className="text-muted-foreground">{noCredentials}</span>
        </div>
      )}

      {data && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-muted-foreground mb-0.5">Display Name</p>
            <p className="font-semibold text-foreground">{data.displayName ?? '-'}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Job Title</p>
            <p className="text-foreground">{data.jobTitle ?? '-'}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Department</p>
            <p className="text-foreground">{data.department ?? '-'}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Account</p>
            <p className="font-semibold">
              {data.accountEnabled === false
                ? <span className="text-red-600 dark:text-red-400">Disabled</span>
                : <span className="text-green-600 dark:text-green-400">Enabled</span>}
            </p>
          </div>

          <div>
            <p className="text-muted-foreground mb-0.5">Risk Level</p>
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${risk.cls}`}>
              {risk.label}
            </span>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">MFA</p>
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
              data.mfaRegistered === true
                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                : data.mfaRegistered === false
                  ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}>
              {data.mfaRegistered === true ? 'Registered' : data.mfaRegistered === false ? 'Not Registered' : 'Unknown'}
            </span>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Sign-ins (24h)</p>
            <p className="font-semibold text-foreground">{data.recentSignInCount24h ?? 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Last Sign-in</p>
            {data.lastSignIn ? (
              <div className="space-y-0.5">
                <p className="text-foreground font-mono text-[11px]">{data.lastSignIn.ipAddress ?? '-'}</p>
                <p className="text-muted-foreground text-[10px]">{formatLocation(data.lastSignIn.location)}</p>
              </div>
            ) : <p className="text-foreground">-</p>}
          </div>
        </div>
      )}

      {data?.note && (
        <p className="text-[10px] text-muted-foreground mt-2 italic">{data.note}</p>
      )}
    </div>
  );
}
